/**
 * Accesso al pannello con il form della pagina (docs/07-admin-e-bot.md §1), senza la pagina di
 * login di Cognito: chiamate dirette all'API di Cognito (`cognito-idp`), che per questi metodi
 * non richiede credenziali AWS. Flusso `USER_PASSWORD_AUTH` (la password viaggia solo in HTTPS
 * verso Cognito), poi le sfide: password nuova al primo accesso (invito), codice dell'app di
 * autenticazione se la persona ha attivato i due fattori. I token restano nel browser
 * (`localStorage`) per la durata del refresh token; l'id token (1 ora) si rinnova da solo.
 */

export interface CognitoConfig {
  region: string;
  clientId: string;
}

export class CognitoError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message || code);
  }
}

async function call<T>(cfg: CognitoConfig, action: string, body: object): Promise<T> {
  const res = await fetch(`https://cognito-idp.${cfg.region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'content-type': 'application/x-amz-json-1.1',
      'x-amz-target': `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const code = String(data.__type ?? `http_${res.status}`)
      .split('#')
      .pop()!;
    throw new CognitoError(code, String(data.message ?? data.Message ?? ''));
  }
  return data as T;
}

// --- sessione ------------------------------------------------------------------------------

export interface AdminSession {
  email: string;
  idToken: string;
  accessToken: string;
  refreshToken: string;
  /** scadenza dell'id token (ms) */
  expiresAt: number;
}

interface AuthResult {
  IdToken: string;
  AccessToken: string;
  RefreshToken?: string;
  ExpiresIn: number;
}

const KEY = 'sse.admin.session';

export function loadSession(): AdminSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as AdminSession) : null;
  } catch {
    return null;
  }
}

export function storeSession(s: AdminSession | null) {
  try {
    if (s) localStorage.setItem(KEY, JSON.stringify(s));
    else localStorage.removeItem(KEY);
  } catch {
    // archiviazione bloccata: la sessione dura quanto la pagina
  }
}

const sessionFrom = (email: string, r: AuthResult, refreshToken?: string): AdminSession => ({
  email,
  idToken: r.IdToken,
  accessToken: r.AccessToken,
  refreshToken: r.RefreshToken ?? refreshToken ?? '',
  expiresAt: Date.now() + r.ExpiresIn * 1000,
});

/** Sessione con l'id token valido almeno per un altro minuto; null se il refresh non va più. */
export async function freshSession(
  cfg: CognitoConfig,
  s: AdminSession,
): Promise<AdminSession | null> {
  if (s.expiresAt - Date.now() > 60_000) return s;
  try {
    const r = await call<{ AuthenticationResult: AuthResult }>(cfg, 'InitiateAuth', {
      ClientId: cfg.clientId,
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      AuthParameters: { REFRESH_TOKEN: s.refreshToken },
    });
    const next = sessionFrom(s.email, r.AuthenticationResult, s.refreshToken);
    storeSession(next);
    return next;
  } catch (e) {
    if (e instanceof CognitoError && e.code === 'NotAuthorizedException') {
      storeSession(null);
      return null;
    }
    throw e;
  }
}

export async function signOut(cfg: CognitoConfig, s: AdminSession | null) {
  storeSession(null);
  if (s?.refreshToken)
    await call(cfg, 'RevokeToken', { ClientId: cfg.clientId, Token: s.refreshToken }).catch(
      () => {},
    );
}

// --- accesso e sfide ------------------------------------------------------------------------

/** Passo successivo dell'accesso: sessione pronta oppure una sfida da completare. */
export type SignInStep =
  | { kind: 'done'; session: AdminSession }
  | { kind: 'totp' | 'new_password'; email: string; username: string; challenge: string };

interface ChallengeResponse {
  AuthenticationResult?: AuthResult;
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: Record<string, string>;
}

function stepOf(email: string, r: ChallengeResponse, username = email): SignInStep {
  if (r.AuthenticationResult) {
    const session = sessionFrom(email, r.AuthenticationResult);
    storeSession(session);
    return { kind: 'done', session };
  }
  const user = r.ChallengeParameters?.USER_ID_FOR_SRP ?? username;
  if (r.ChallengeName === 'SOFTWARE_TOKEN_MFA')
    return { kind: 'totp', email, username: user, challenge: r.Session! };
  if (r.ChallengeName === 'NEW_PASSWORD_REQUIRED')
    return { kind: 'new_password', email, username: user, challenge: r.Session! };
  throw new CognitoError(
    'UnsupportedChallenge',
    `Passaggio di accesso non gestito: ${r.ChallengeName}`,
  );
}

export async function signIn(cfg: CognitoConfig, email: string, password: string) {
  const r = await call<ChallengeResponse>(cfg, 'InitiateAuth', {
    ClientId: cfg.clientId,
    AuthFlow: 'USER_PASSWORD_AUTH',
    AuthParameters: { USERNAME: email, PASSWORD: password },
  });
  return stepOf(email, r);
}

async function respond(
  cfg: CognitoConfig,
  step: Extract<SignInStep, { challenge: string }>,
  name: string,
  responses: Record<string, string>,
) {
  const r = await call<ChallengeResponse>(cfg, 'RespondToAuthChallenge', {
    ClientId: cfg.clientId,
    ChallengeName: name,
    Session: step.challenge,
    ChallengeResponses: { USERNAME: step.username, ...responses },
  });
  return stepOf(step.email, r, step.username);
}

export const answerTotp = (
  cfg: CognitoConfig,
  step: Extract<SignInStep, { challenge: string }>,
  code: string,
) => respond(cfg, step, 'SOFTWARE_TOKEN_MFA', { SOFTWARE_TOKEN_MFA_CODE: code });

export const answerNewPassword = (
  cfg: CognitoConfig,
  step: Extract<SignInStep, { challenge: string }>,
  password: string,
) => respond(cfg, step, 'NEW_PASSWORD_REQUIRED', { NEW_PASSWORD: password });

export const forgotPassword = (cfg: CognitoConfig, email: string) =>
  call(cfg, 'ForgotPassword', { ClientId: cfg.clientId, Username: email });

export const confirmForgotPassword = (
  cfg: CognitoConfig,
  email: string,
  code: string,
  password: string,
) =>
  call(cfg, 'ConfirmForgotPassword', {
    ClientId: cfg.clientId,
    Username: email,
    ConfirmationCode: code,
    Password: password,
  });

// --- sicurezza dell'account (con l'access token) --------------------------------------------

export interface AccountSecurity {
  totp: boolean;
}

export async function accountSecurity(
  cfg: CognitoConfig,
  accessToken: string,
): Promise<AccountSecurity> {
  const r = await call<{ UserMFASettingList?: string[] }>(cfg, 'GetUser', {
    AccessToken: accessToken,
  });
  return { totp: (r.UserMFASettingList ?? []).includes('SOFTWARE_TOKEN_MFA') };
}

/** Nuovo segreto per l'app di autenticazione (diventa attivo solo dopo la verifica). */
export async function startTotpSetup(cfg: CognitoConfig, accessToken: string) {
  const r = await call<{ SecretCode: string }>(cfg, 'AssociateSoftwareToken', {
    AccessToken: accessToken,
  });
  return r.SecretCode;
}

/** Verifica il primo codice e attiva i due fattori come metodo preferito. */
export async function confirmTotpSetup(cfg: CognitoConfig, accessToken: string, code: string) {
  await call(cfg, 'VerifySoftwareToken', {
    AccessToken: accessToken,
    UserCode: code,
    FriendlyDeviceName: 'App di autenticazione',
  });
  await setTotp(cfg, accessToken, true);
}

export const setTotp = (cfg: CognitoConfig, accessToken: string, enabled: boolean) =>
  call(cfg, 'SetUserMFAPreference', {
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: { Enabled: enabled, PreferredMfa: enabled },
  });

export const changePassword = (
  cfg: CognitoConfig,
  accessToken: string,
  previous: string,
  proposed: string,
) =>
  call(cfg, 'ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previous,
    ProposedPassword: proposed,
  });

/** Indirizzo `otpauth://` per il QR dell'app di autenticazione. */
export function otpauthUri(secret: string, email: string, issuer: string) {
  const label = encodeURIComponent(`${issuer}:${email}`);
  const q = new URLSearchParams({ secret, issuer, algorithm: 'SHA1', digits: '6', period: '30' });
  return `otpauth://totp/${label}?${q.toString()}`;
}

/** Messaggi in italiano per gli errori di Cognito più comuni. */
export function cognitoMessage(e: unknown): string {
  if (!(e instanceof CognitoError)) return e instanceof Error ? e.message : String(e);
  switch (e.code) {
    case 'NotAuthorizedException':
      return /attempts exceeded/i.test(e.message)
        ? 'Troppi tentativi: riprova tra qualche minuto.'
        : /session is expired|Invalid session/i.test(e.message)
          ? 'Il tempo per completare l’accesso è scaduto: ricomincia.'
          : 'Email o password non corrette.';
    case 'CodeMismatchException':
      return 'Codice non corretto.';
    case 'ExpiredCodeException':
      return 'Codice scaduto: chiedine uno nuovo.';
    case 'EnableSoftwareTokenMFAException':
      return 'Codice non corretto: controlla l’ora del telefono e riprova.';
    case 'InvalidPasswordException':
      return 'La password deve avere almeno 12 caratteri, con maiuscole, minuscole, numeri e simboli.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Troppi tentativi: riprova tra qualche minuto.';
    case 'UserNotFoundException':
      return 'Email o password non corrette.';
    case 'PasswordResetRequiredException':
      return 'Devi impostare una password nuova: usa «Password dimenticata».';
    default:
      return e.message || e.code;
  }
}
